import { useEffect, useMemo, useState } from 'react';
import { useEditor } from '../state/store';
import { Icon } from '../ui/icons';
import { useCall, type CallContextValue, type CallParticipantView } from './CallProvider';
import { ParticipantChips, ParticipantTile } from './ParticipantTile';
import { useFloatingWindow } from './useFloatingWindow';
import { usePopoutWindow } from './usePopoutWindow';

/**
 * How much of the call is on screen.
 *
 *   pill      a thin strip of faces — enough to see who is there while the
 *             board has the floor
 *   window    the default: a small movable, resizable window
 *   expanded  a full-screen stage for looking at everyone properly
 */
type CallView = 'pill' | 'window' | 'expanded';

const PILL_SIZE = { width: 300, height: 56 };
const MIN_SIZE = { width: 264, height: 232 };

function viewKey(boardId: string): string {
  return `kanvas.call-view.${boardId}`;
}

function loadView(boardId: string): CallView {
  try {
    const raw = localStorage.getItem(viewKey(boardId));
    if (raw === 'pill' || raw === 'window' || raw === 'expanded') return raw;
  } catch {
    // Blocked storage — the default window is a fine starting point.
  }
  return 'window';
}

function participantsLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} участник`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} участника`;
  return `${n} участников`;
}

/** Squarer grids read better than long rows once there are more than a few faces. */
function columnsFor(count: number): number {
  if (count <= 1) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  return 4;
}

/**
 * The call window.
 *
 * It is a window rather than a fixed dock — it moves, resizes and remembers
 * where it was left — because an infinite canvas has no corner that is out of
 * the way for everybody. The control bar is present in every view on purpose:
 * whatever is being shown, the buttons that end the call, mute the microphone
 * or stop a screen share must never be a mode away.
 */
export function CallWindow({ boardId }: { boardId: string }) {
  const call = useCall();
  const presentingFrames = useEditor((s) => s.presentingFrameId !== null);
  const [view, setView] = useState<CallView>(() => loadView(boardId));
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const floating = useFloatingWindow(`kanvas.call-window.${boardId}`, {
    defaultRect: ({ width, height }) => ({
      x: Math.max(12, width - 356),
      y: Math.max(12, height - 396),
      width: 344,
      height: 344,
    }),
    minWidth: MIN_SIZE.width,
    minHeight: MIN_SIZE.height,
    clampSize: view === 'pill' ? PILL_SIZE : undefined,
  });

  // A frame deck takes over the screen, so the call steps back to a strip
  // until whoever is watching decides otherwise.
  useEffect(() => {
    if (presentingFrames) setView('pill');
  }, [presentingFrames]);

  useEffect(() => {
    try {
      localStorage.setItem(viewKey(boardId), view);
    } catch {
      // Non-fatal: the view just resets on the next reload.
    }
  }, [boardId, view]);

  useEffect(() => {
    if (view !== 'expanded') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setView('window');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view]);

  const participants = call.participants;
  const focused = useMemo(
    () => participants.find((p) => p.identity === focusedId) ?? null,
    [participants, focusedId],
  );

  const connected = call.status === 'connected' || call.status === 'reconnecting';

  // A separate OS window, so the call can sit next to the browser instead of
  // on top of the board — the point being that the board is what people are
  // working on while they talk.
  const popout = usePopoutWindow({
    title: 'Звонок',
    name: 'kanvas-call',
    width: 420,
    height: 460,
  });
  const closePopout = popout.close;
  useEffect(() => {
    if (call.status === 'idle' || call.status === 'error') closePopout();
  }, [call.status, closePopout]);

  // A failed attempt stays on screen. Hiding it was how the call became
  // unrecoverable: the error had nowhere to appear and no way to be retried.
  if (call.status === 'error') {
    return (
      <div className="call-pill is-error" style={{ left: floating.rect.x, top: floating.rect.y }} role="alert">
        <span className="call-pill-grip" {...floating.dragHandlers}>
          <span className="call-live-dot" />
          <span className="call-error-text">{call.error ?? 'Не удалось подключиться к звонку.'}</span>
        </span>
        <div className="call-pill-actions">
          <button
            type="button"
            className="call-btn is-small"
            onClick={() => void call.join()}
            aria-label="Повторить попытку"
            data-tip="Повторить"
          >
            <Icon name="refresh" size={14} />
          </button>
          <button
            type="button"
            className="call-btn is-small"
            onClick={() => void call.leave()}
            aria-label="Закрыть"
            data-tip="Закрыть"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (!connected && call.status !== 'connecting') return null;

  const statusText =
    call.status === 'connecting'
      ? 'Подключение…'
      : call.status === 'reconnecting'
        ? 'Переподключение…'
        : participantsLabel(participants.length);

  const toggleFocus = (participant: CallParticipantView) =>
    setFocusedId((current) => (current === participant.identity ? null : participant.identity));

  // Detached, the page keeps a marker: a call whose window has gone missing
  // behind other windows must still be findable and endable from the board.
  if (popout.open) {
    return (
      <>
        <div className="call-pill is-detached" style={{ left: floating.rect.x, top: floating.rect.y }}>
          <div className="call-pill-grip" {...floating.dragHandlers}>
            <span className={`call-live-dot ${connected ? 'is-live' : ''}`} />
            <span className="call-pill-status">Звонок в отдельном окне</span>
          </div>
          <div className="call-pill-actions">
            <button
              type="button"
              className="call-btn is-small"
              onClick={popout.close}
              aria-label="Вернуть звонок в страницу"
              data-tip="Вернуть в страницу"
            >
              <Icon name="popin" size={14} />
            </button>
          </div>
        </div>

        {popout.render(
          <div className="call-window is-popped" role="group" aria-label="Звонок">
            <header className="call-bar">
              <CallStatus text={statusText} live={connected} />
              <div className="call-bar-actions">
                <button
                  type="button"
                  className="call-bar-btn"
                  onClick={popout.close}
                  aria-label="Вернуть звонок в страницу"
                  data-tip="Вернуть в страницу"
                >
                  <Icon name="popin" size={15} />
                </button>
              </div>
            </header>

            <div className="call-grid" style={{ ['--cols' as string]: columnsFor(participants.length) }}>
              {participants.map((p) => (
                <ParticipantTile key={p.identity} participant={p} />
              ))}
            </div>

            <CallControls call={call} compact />

            {call.error && <div className="call-error" role="alert">{call.error}</div>}
          </div>,
        )}
      </>
    );
  }

  if (view === 'expanded') {
    return (
      <div className="call-expanded" role="dialog" aria-label="Звонок">
        <div className="call-expanded-panel">
          <header className="call-bar">
            <CallStatus text={statusText} live={connected} />
            <div className="call-bar-actions">
              <button
                type="button"
                className="call-bar-btn"
                onClick={() => setView('window')}
                aria-label="Свернуть в окно"
                data-tip="Свернуть в окно"
              >
                <Icon name="minimize" size={15} />
              </button>
            </div>
          </header>

          {focused ? (
            <div className="call-stage">
              <ParticipantTile participant={focused} variant="stage" focused onSelect={() => setFocusedId(null)} />
              {participants.length > 1 && (
                <div className="call-strip">
                  {participants
                    .filter((p) => p.identity !== focused.identity)
                    .map((p) => (
                      <ParticipantTile
                        key={p.identity}
                        participant={p}
                        variant="strip"
                        onSelect={() => toggleFocus(p)}
                      />
                    ))}
                </div>
              )}
            </div>
          ) : (
            <div className="call-grid" style={{ ['--cols' as string]: columnsFor(participants.length) }}>
              {participants.map((p) => (
                <ParticipantTile key={p.identity} participant={p} onSelect={() => toggleFocus(p)} />
              ))}
            </div>
          )}

          <CallControls call={call} />

          {call.error && <div className="call-error" role="alert">{call.error}</div>}
        </div>
      </div>
    );
  }

  const { rect, dragging, dragHandlers, resizeHandlers } = floating;

  if (view === 'pill') {
    return (
      <div
        className={`call-pill ${dragging ? 'is-dragging' : ''}`}
        style={{ left: rect.x, top: rect.y }}
        role="group"
        aria-label="Звонок"
      >
        <div className="call-pill-grip" {...dragHandlers}>
          <span className={`call-live-dot ${connected ? 'is-live' : ''}`} />
          <span className="call-pill-status" title={call.error ?? undefined}>
            {call.error ?? statusText}
          </span>
        </div>
        <ParticipantChips participants={participants} />
        <div className="call-pill-actions">
          <button
            type="button"
            className={`call-btn is-small ${call.localMicOn ? '' : 'is-off'}`}
            onClick={() => void call.toggleMic()}
            aria-label={call.localMicOn ? 'Выключить микрофон' : 'Включить микрофон'}
            data-tip={call.localMicOn ? 'Выключить микрофон' : 'Включить микрофон'}
          >
            <Icon name={call.localMicOn ? 'mic' : 'micOff'} size={14} />
          </button>
          <button
            type="button"
            className="call-btn is-small"
            onClick={() => setView('window')}
            aria-label="Развернуть окно звонка"
            data-tip="Развернуть"
          >
            <Icon name="maximize" size={14} />
          </button>
          <button
            type="button"
            className="call-btn is-small is-leave"
            onClick={() => void call.leave()}
            aria-label="Покинуть звонок"
            data-tip="Покинуть звонок"
          >
            <Icon name="phone" size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`call-window ${dragging ? 'is-dragging' : ''}`}
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
      role="group"
      aria-label="Звонок"
    >
      <header className="call-bar" {...dragHandlers}>
        <CallStatus text={statusText} live={connected} />
        <div className="call-bar-actions">
          {popout.supported && (
            <button
              type="button"
              className="call-bar-btn"
              onClick={popout.requestOpen}
              aria-label="Открыть звонок отдельным окном"
              data-tip="Отдельное окно"
            >
              <Icon name="popout" size={15} />
            </button>
          )}
          <button
            type="button"
            className="call-bar-btn"
            onClick={() => setView('expanded')}
            aria-label="Развернуть на весь экран"
            data-tip="На весь экран"
          >
            <Icon name="maximize" size={15} />
          </button>
          <button
            type="button"
            className="call-bar-btn"
            onClick={() => setView('pill')}
            aria-label="Свернуть звонок"
            data-tip="Свернуть"
          >
            <Icon name="minimize" size={15} />
          </button>
        </div>
      </header>

      <div className="call-grid" style={{ ['--cols' as string]: columnsFor(participants.length) }}>
        {participants.map((p) => (
          <ParticipantTile key={p.identity} participant={p} onSelect={() => setView('expanded')} />
        ))}
      </div>

      <CallControls call={call} compact />

      {call.error && <div className="call-error" role="alert">{call.error}</div>}

      <span className="window-resize" aria-hidden="true" {...resizeHandlers} />
    </div>
  );
}

function CallStatus({ text, live }: { text: string; live: boolean }) {
  return (
    <span className="call-bar-title">
      <span className={`call-live-dot ${live ? 'is-live' : ''}`} />
      {text}
    </span>
  );
}

/**
 * Everything that changes what other people see or hear, in one bar that is
 * rendered in every view — including while a screen is being shared, which is
 * exactly when "stop sharing" has to be one click away.
 */
function CallControls({ call, compact = false }: { call: CallContextValue; compact?: boolean }) {
  return (
    <footer className={`call-controls ${compact ? 'is-compact' : ''}`}>
      {call.audioBlocked && (
        <button type="button" className="call-unmute" onClick={() => void call.enableAudio()}>
          <Icon name="mic" size={13} /> Включить звук
        </button>
      )}

      <button
        type="button"
        className={`call-btn ${call.localMicOn ? '' : 'is-off'}`}
        onClick={() => void call.toggleMic()}
        aria-pressed={call.localMicOn}
        aria-label={call.localMicOn ? 'Выключить микрофон' : 'Включить микрофон'}
        data-tip={call.localMicOn ? 'Выключить микрофон' : 'Включить микрофон'}
      >
        <Icon name={call.localMicOn ? 'mic' : 'micOff'} size={17} />
      </button>

      <button
        type="button"
        className={`call-btn ${call.localCamOn ? 'is-on' : ''}`}
        onClick={() => void call.toggleCam()}
        aria-pressed={call.localCamOn}
        aria-label={call.localCamOn ? 'Выключить камеру' : 'Включить камеру'}
        data-tip={call.localCamOn ? 'Выключить камеру' : 'Включить камеру'}
      >
        <Icon name={call.localCamOn ? 'video' : 'videoOff'} size={17} />
      </button>

      {call.canFlipCamera && (
        <button
          type="button"
          className="call-btn"
          onClick={() => void call.flipCamera()}
          aria-label={
            call.cameraFacing === 'user' ? 'Переключить на заднюю камеру' : 'Переключить на переднюю камеру'
          }
          data-tip={call.cameraFacing === 'user' ? 'Задняя камера' : 'Передняя камера'}
        >
          <Icon name="flipCamera" size={17} />
        </button>
      )}

      <button
        type="button"
        className={`call-btn ${call.chatOpen ? 'is-on' : ''}`}
        onClick={call.toggleChat}
        aria-pressed={call.chatOpen}
        aria-label={call.chatOpen ? 'Закрыть чат' : 'Открыть чат звонка'}
        data-tip={call.chatOpen ? 'Закрыть чат' : 'Чат'}
      >
        <Icon name="comment" size={17} />
        {call.unreadChat > 0 && (
          // One atomic status rather than a bare live number, which a screen
          // reader would otherwise announce as a meaningless "3".
          <span className="call-badge" role="status" aria-atomic="true">
            <span aria-hidden="true">{call.unreadChat > 9 ? '9+' : call.unreadChat}</span>
            <span className="sr-only">{`Новых сообщений в чате: ${call.unreadChat}`}</span>
          </span>
        )}
      </button>

      <button
        type="button"
        className={`call-btn ${call.isScreenSharing ? 'is-sharing' : ''}`}
        onClick={() => void call.toggleScreenShare()}
        disabled={Boolean(call.screenShare && !call.isScreenSharing)}
        aria-pressed={call.isScreenSharing}
        aria-label={
          call.isScreenSharing
            ? 'Остановить демонстрацию экрана'
            : call.screenShare
              ? `Экран уже показывает ${call.screenShare.name}`
              : 'Демонстрация экрана'
        }
        data-tip={
          call.isScreenSharing
            ? 'Остановить показ экрана'
            : call.screenShare
              ? `${call.screenShare.name} уже показывает экран`
              : 'Показать экран'
        }
      >
        <Icon name="share" size={17} />
      </button>

      <button
        type="button"
        className="call-btn is-leave"
        onClick={() => void call.leave()}
        aria-label="Покинуть звонок"
        data-tip="Покинуть звонок"
      >
        <Icon name="phone" size={17} />
      </button>
    </footer>
  );
}

/** Toggles the whole call for the topbar: joins when idle, leaves once connected. */
export function useCallButtonState() {
  const call = useCall();
  return useMemo(
    () => ({
      inCall: call.status === 'connected' || call.status === 'connecting' || call.status === 'reconnecting',
      configured: call.configured,
      onClick: () => {
        // `error` has to lead back to `join`, not to `leave`: leaving a call
        // that never connected did nothing, which left the button dead.
        if (call.status === 'idle' || call.status === 'error') void call.join();
        else void call.leave();
      },
    }),
    [call],
  );
}
